import {
  Firestore,
  query,
  where,
  getCount,
  getDocs,
  Timestamp,
} from "firebase/firestore/lite";
import { bookingService } from "./service";
import { DateTime } from "luxon";
import { firebaseDb } from "../firebase/client-app";
import { DbVisit, DbVisitType } from "../visit/models";
import { bookingVisitsService } from "./visits-service";
import { DbServiceBookingStatus } from "./models";

export class BookingAnalyticsService {
  async getUserBookingCountsForBusiness(
    businessId: string,
    userId: string,
    recent: boolean = false,
    firestore: Firestore = firebaseDb,
  ): Promise<number> {
    let q = query(
      bookingVisitsService.visitRef(firestore),
      where("business_id", "==", businessId),
      where("user_id", "==", userId),
    );
    if (recent) {
      const now = Timestamp.fromDate(DateTime.now().toJSDate());
      q = query(
        bookingVisitsService.visitRef(firestore),
        where("business_id", "==", businessId),
        where("user_id", "==", userId),
        where("booking_date", ">=", now),
      );
    }
    const bookings = await getCount(q);
    return bookings.data().count;
  }

  async getUserBookingRevenueForBusiness(
    businessId: string,
    userId?: string,
    firestore: Firestore = firebaseDb,
  ): Promise<number> {
    if (userId == null) return 0;
    const visits = await bookingVisitsService.getUserVisitsForBusiness(
      businessId,
      userId,
      null,
      null,
      firestore,
    );
    return visits.reduce(
      (acc, visit) => acc + (visit.financial_summary.total_amount || 0),
      0,
    );
  }

  async getRepeatClientsForBusiness(
    businessId: string,
    startDate: Timestamp,
    endDate: Timestamp,
    firestore: Firestore = firebaseDb,
  ): Promise<number> {
    const q = query(
      bookingVisitsService.visitRef(firestore),
      where("business_id", "==", businessId),
      where("booking_date", ">=", startDate),
      where("booking_date", "<=", endDate),
    );

    const bookingsSnapshot = await getDocs(q);

    const userBookings = bookingsSnapshot.docs.reduce(
      (acc, doc) => {
        const data = doc.data();
        if (data.user_id) {
          acc[data.user_id] = acc[data.user_id] || 0;
          acc[data.user_id]++;
        }
        return acc;
      },
      {} as Record<string, number>,
    );

    const repeatClients = Object.values(userBookings).filter(
      (count) => count > 1,
    );

    return repeatClients.length;
  }

  async getBookingCounts(
    businessId: string,
    startDate?: Timestamp | null,
    endDate?: Timestamp | null,
    firestore: Firestore = firebaseDb,
  ): Promise<number> {
    let q = query(
      bookingVisitsService.visitRef(firestore),
      where("business_id", "==", businessId),
    );
    if (startDate && endDate) {
      q = query(
        q,
        where("booking_date", ">=", startDate),
        where("booking_date", "<=", endDate),
        where("status", "==", DbServiceBookingStatus.confirmed),
      );
    }
    const bookings = await getCount(q);
    return bookings.data().count;
  }

  async getAdvanceBookingCounts(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<number> {
    const q = query(
      bookingVisitsService.visitRef(firestore),
      where("business_id", "==", businessId),
      where("type", "==", DbVisitType.Advance),
    );
    const bookings = await getCount(q);
    return bookings.data().count;
  }

  async getBookingsRevenueOfBusiness(
    businessId: string,
    startDate: DateTime,
    endDate: DateTime,
    firestore: Firestore = firebaseDb,
  ) {
    let visitsQuery = query(
      bookingVisitsService.visitRef(firestore),
      where("business_id", "==", businessId),
      where("booking_date", ">=", Timestamp.fromDate(startDate.toJSDate())),
      where("booking_date", "<=", Timestamp.fromDate(endDate.toJSDate())),
    );

    const visitsDocs = await getDocs(visitsQuery);
    const visits = visitsDocs.docs.map((d) => d.data() as DbVisit);

    let totalRevenue = 0;

    for (const visit of visits) {
      if (visit.type === DbVisitType.WalkIn) {
        totalRevenue += visit.financial_summary.total_amount || 0;
      } else {
        const bookings = await Promise.all(
          visit.services
            .filter((service) => service.booking_id)
            .map((service) => bookingService.getBooking(service.booking_id)),
        );

        const visitRevenue = bookings
          .filter(
            (booking) =>
              booking &&
              !booking.payment_membership_id &&
              booking.booking_status === DbServiceBookingStatus.confirmed,
          )
          .reduce((acc, booking) => acc + (booking?.booking_amount || 0), 0);

        totalRevenue += visitRevenue;
      }
    }

    return totalRevenue;
  }
}

export const bookingAnalyticsService = new BookingAnalyticsService();
